import { Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import {
  ChevronDownIcon,
  CloseIcon,
  PlusIcon,
  SearchIcon,
  TargetIcon,
} from '../../assets/icons/index';
import { AlertDialogWrapper } from '../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import { EmptySearchList } from '../../components/empty-search-list/index';
import { Input } from '../../components/input/index';
import { LoadingCrossfade } from '../../components/loading-skeleton/index';
import { StaggerFadeIn, StaggerItem } from '../../components/motion/index';
import { Select, SelectButton, SelectOption, SelectOptions } from '../../components/select/index';
import { Sheet, SheetContent, SheetTrigger } from '../../components/sheet/index';
import { QueryKey } from '../../constants/index';
import {
  ScenarioHooks,
  useDebounce,
  useModalState,
  useScreenSize,
  useSelectedCompany,
} from '../../hooks/index';
import { PageRoute } from '../../types/page.types';
import { Scenario, ScenarioType, ScenarioTypeTitle } from '../../types/scenario-modeling.types';
import { AccessRole } from '../../types/stakeholder.types';
import { useScenarioCapTableStore } from './cap-table.store';
import { EmptyScenarioModeling } from './empty-scenario-modeling';
import { SaveModal } from './modals/index';
import { ScenarioCard } from './scenario-card';
import { ScenarioModelingPlaceholder } from './scenario-modeling-loading-placeholders';

const ScenarioModeling: FC = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id || '';
  const [search, setSearch] = useState<null | string>(null);
  const [isOpenSearch, setOpenSearch] = useState<boolean>(false);
  const [isFocusedInput, setInputFocus] = useState<boolean>(false);
  const [selected, setSelected] = useState<ScenarioType>(ScenarioType.FUNDRAISING);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isFirstLoad = useRef(true);

  const selectVariant = Object.values(ScenarioType);

  const { create, isPending } = ScenarioHooks.useCreate();
  const { scenarios, invalidateQuery, isLoading, refetch } = ScenarioHooks.useScenarios({
    companyId,
    search: search || '',
  });

  const { scheduleUpdateBatch } = useScenarioCapTableStore();

  useEffect(() => {
    if (!scenarios?.length) return;

    scheduleUpdateBatch(scenarios);
  }, [scheduleUpdateBatch, scenarios]);

  const scenarioNames = scenarios?.map((scenario: Scenario) =>
    scenario.name?.trim()?.toLowerCase(),
  );

  const {
    toggler: saveModalToggler,
    isOpen: isOpenSaveModal,
    handleOpenModal: handleOpenSaveModal,
    handleCloseModal: handleCloseSaveModal,
    handleSuccessModal: handleSuccessSaveModal,
  } = useModalState<string | undefined, unknown, string>({
    onSuccess: (name?: string) => {
      create(
        {
          data: {
            name: name || '',
            type: selected || ScenarioType.FUNDRAISING,
            currentCapTable: true,
          },
        },
        {
          onSuccess: (data) => {
            if (data?.id) {
              queryClient.setQueryData([QueryKey.GET_SCENARIO, data.id], data);
            }
            navigate(`${PageRoute.SCENARIO_MODELING}/${data?.id}`);
          },
        },
      );
    },
  });

  const handleSelect = (select: ScenarioType) => {
    setSelected(select);
    handleOpenSaveModal();
  };

  const { debounceCallback } = useDebounce({
    callback: () => {
      invalidateQuery();
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      debounceCallback(value);
      setIsDebouncing(true);
    },
    [debounceCallback],
  );

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    if (!search && search !== null) {
      refetch();
    }
  }, [companyId, refetch, search]);

  const { width } = useScreenSize();
  const isMobile = width < 992;
  const isAuditor = selectedCompany?.stakeholder?.accessRole === AccessRole.AUDITOR;
  const canCreateScenario = !isAuditor;

  return (
    <LoadingCrossfade isLoading={isLoading} skeleton={<ScenarioModelingPlaceholder />}>
      {() => (
        <>
          <AlertDialogWrapper control={{ onOpenChange: saveModalToggler, open: isOpenSaveModal }}>
            <SaveModal
              isPending={isPending}
              onClose={handleCloseSaveModal}
              onSuccess={handleSuccessSaveModal}
              scenarioNames={scenarioNames}
              scenarios={scenarios}
              type={selected}
            />
          </AlertDialogWrapper>
          <div className="flex w-full flex-col gap-4 xlg:min-h-[86vh]">
            {isMobile ? (
              <div className="flex items-center gap-2 border-b-[1px] border-gray-100 pb-5">
                <TargetIcon />
                <span className="text-base font-semibold text-gray-700">
                  <AppFormattedMessage id="MY_SCENARIOS" />
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-md bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <TargetIcon />
                  <span className="text-base font-semibold text-gray-700">
                    <AppFormattedMessage id="MY_SCENARIOS" />
                  </span>
                </div>
                <div className="relative flex h-full w-fit items-center gap-3">
                  <Sheet modal={false} open={isMobile && isOpenSearch}>
                    <div className="flex items-center justify-end" ref={inputRef}>
                      <Input
                        inputClassName="h-9"
                        onBlur={() => setInputFocus(false)}
                        onChange={handleSearch}
                        onFocus={() => {
                          setInputFocus(true);
                        }}
                        placeholder={
                          search || isFocusedInput ? '' : <AppFormattedMessage id="SEARCH" />
                        }
                        value={search || ''}
                        wrapperClassName={twMerge(
                          'transition-all duration-500 w-[200px] overflow-hidden h-9',
                          isOpenSearch ? 'delay-500' : 'w-0',
                          isMobile && 'w-0',
                        )}
                      />
                      {!isMobile && isOpenSearch && typeof search === 'string' && (
                        <div
                          className="absolute mr-8 cursor-pointer p-2"
                          onClick={() => {
                            setSearch('');
                            invalidateQuery();
                          }}
                        >
                          <CloseIcon />
                        </div>
                      )}
                      <SheetTrigger
                        className={twMerge(
                          'flex size-9 items-center justify-center rounded border-[1px] border-gray-100 bg-gray-50 transition-all duration-500',
                          !isMobile && isOpenSearch
                            ? 'h-9 rounded-l-none rounded-br-none border-x-0 border-t-0 border-b-black'
                            : 'delay-1000',
                          !isMobile &&
                            isFocusedInput &&
                            'border-b-[2px] border-b-brand-700 transition-none',
                        )}
                        onClick={() => {
                          setOpenSearch((prev) => !prev);
                          setSearch((prev) => (prev ? '' : null));
                          // invalidateQuery();
                        }}
                      >
                        <div className="transition-colors duration-500">
                          <SearchIcon iconColor={isFocusedInput ? '#2565C8' : '#344054'} />
                        </div>
                      </SheetTrigger>
                    </div>
                    <SheetContent
                      className="w-full"
                      onClick={(e) => e.stopPropagation()}
                      onInteractOutside={() => {
                        if (!search) {
                          setOpenSearch(false);
                          setSearch(null);
                          invalidateQuery();
                        }
                      }}
                      side="TOP"
                    >
                      <div className={twMerge('flex')}>
                        <Input
                          icon={search && <CloseIcon />}
                          onBlur={() => setInputFocus(false)}
                          onChange={handleSearch}
                          onFocus={(e) => {
                            setInputFocus(true);
                            e.stopPropagation();
                          }}
                          onIconClick={() => {
                            setSearch(null);
                            // invalidateQuery();
                          }}
                          placeholder={<AppFormattedMessage id="SEARCH" />}
                          value={search || ''}
                          wrapperClassName={twMerge('transition-all duration-1000 w-full')}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>
                  {canCreateScenario && (
                    <Select onChange={handleSelect} value={selected}>
                      {({ open }) => (
                        <>
                          <div className="relative rounded border-gray-200">
                            <SelectButton className="w-[147px] bg-brand-700">
                              <PlusIcon />
                              <span className="text-sm font-[450] text-white">
                                <AppFormattedMessage id="SCENARIO" />
                              </span>
                              <ChevronDownIcon className="ml-2 h-4 w-4" iconColor="#ffffff" />
                            </SelectButton>

                            <Transition
                              as="div"
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              show={open}
                            >
                              <SelectOptions className="w-[147px]">
                                {selectVariant.map((select) => (
                                  <SelectOption
                                    className="px-0"
                                    key={select}
                                    selectedClassName="bg-white"
                                    value={select}
                                  >
                                    <span className="block text-sm font-normal text-gray-700">
                                      <AppFormattedMessage id={ScenarioTypeTitle[select]} />
                                    </span>
                                  </SelectOption>
                                ))}
                              </SelectOptions>
                            </Transition>
                          </div>
                        </>
                      )}
                    </Select>
                  )}
                </div>
              </div>
            )}

            {scenarios && scenarios?.length > 0 ? (
              <StaggerFadeIn className="flex w-full flex-wrap gap-4">
                {scenarios.map((scenario) => (
                  <StaggerItem
                    className="w-full xlg:max-w-[286px] lg:max-w-[404px]"
                    key={scenario.id}
                  >
                    <ScenarioCard
                      isMobile={isMobile}
                      scenario={scenario}
                      scenarioNames={scenarioNames}
                    />
                  </StaggerItem>
                ))}
              </StaggerFadeIn>
            ) : (
              search && (
                <div className="flex w-full justify-center pt-[82px]">
                  <EmptySearchList />
                </div>
              )
            )}
            {!isDebouncing && scenarios && scenarios.length === 0 && (
              <EmptyScenarioModeling
                onAddFundraisingClick={handleOpenSaveModal}
                onAddLiquidityClick={handleOpenSaveModal}
                selected={selected}
                setSelected={setSelected}
                showButton={!isMobile && canCreateScenario}
              />
            )}
          </div>
        </>
      )}
    </LoadingCrossfade>
  );
};

export default ScenarioModeling;
